import { useSubscriptionAccessConfigContext } from '@ltvco/refresh-lib/ctx';
import { Box, Divider, Tab, Tabs, useTheme } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { SearchData, SearchTabData } from '@ltvco/refresh-lib/v1';
import {
  FeatureBlockedModal,
  useFeatureBlockedModal,
  MobileTabsToggle,
  SearchFormsContainer,
  useSearchFormContext,
} from '@ltvco/refresh-lib/v2';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type SearchHubProps = {
  searchFrom?: string;
};

export const SearchHub = ({ searchFrom }: SearchHubProps) => {
  const { useSearchForm } = useSearchFormContext();
  const theme = useTheme();
  const {
    handleOpenFormContainer,
    searchTabs,
    activeFormTab,
    handleTabChange,
    setActiveFormTab,
  } = useSearchForm(true);

  const [searchType, setSearchType] = useState<string>('');
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  const [filteredSearchTabs] = useState<SearchTabData[]>([]);
  const { isBlocked } = useSubscriptionAccessConfigContext();
  const {
    isFeatureBlockedModalOpen,
    openFeatureBlockedModal,
    closeFeatureBlockedModal,
  } = useFeatureBlockedModal();

  useEffect(() => {
    if (filteredSearchTabs.length > 0) {
      const firstUnblockedTab =
        filteredSearchTabs.find(
          (tab) => !checkIfFeatureIsBlocked(tab.displayName)
        ) ?? filteredSearchTabs[0];
      setActiveFormTab(firstUnblockedTab.id);
    }
  }, [filteredSearchTabs]);

  const checkIfFeatureIsBlocked = (tabDisplayName: string) => {
    if (tabDisplayName === 'People') {
      return isBlocked('person');
    }
    return isBlocked(tabDisplayName.toLowerCase());
  };

  const navigate = useNavigate();
  const handleSearchSuccess = ({
    reportType,
    searchParams,
    permalink,
  }: SearchData) => {
    const url = `/report/${reportType}?${searchParams}&permalink=${permalink}`;
    navigate(url);
  };
  const { isTablet } = useScreenSize();
  const handleTabClick = (_event: React.SyntheticEvent, tabId: string) => {
    const tab = filteredSearchTabs.find((t) => t.id === tabId);

    if (checkIfFeatureIsBlocked(tab.displayName)) {
      setSearchType(tab.displayName);
      openFeatureBlockedModal();
      return;
    }
    setActiveFormTab(tabId);
  };
  return (
    <Box
      bgcolor="background.default"
      borderRadius={2}
      border="1px solid"
      borderColor="divider"
    >
      {!isTablet && (
        <>
          <Tabs
            value={activeFormTab}
            onChange={handleTabChange}
            TabIndicatorProps={{
              sx: {
                top: 60,
                backgroundColor: theme.palette.text.primary,
              },
            }}
            centered
          >
            {searchTabs.map((tab) => (
              <Tab
                disableRipple
                label={tab.displayName}
                key={tab.id}
                value={tab.id}
                icon={React.cloneElement(tab.icon, {
                  color: theme.palette.text.primary,
                })}
                iconPosition="start"
                onClick={(event) => handleTabClick(event, tab.id)}
                sx={{
                  color: theme.palette.text.primary,
                  '&.Mui-selected': { color: theme.palette.text.primary },
                }}
              />
            ))}
          </Tabs>
          <Divider sx={{ position: 'relative', top: -10 }} />
        </>
      )}
      {isTablet && (
        <Box bgcolor="background.paper">
          <MobileTabsToggle
            activeTab={activeFormTab}
            tabs={searchTabs}
            handleTabChange={handleTabChange}
          />
        </Box>
      )}
      <SearchFormsContainer
        activeTab={activeFormTab}
        searchTabs={searchTabs}
        onSearchSuccess={handleSearchSuccess}
        closeForm={handleOpenFormContainer}
        searchFrom={searchFrom}
      />
      <FeatureBlockedModal
        isOpen={isFeatureBlockedModalOpen}
        onClose={closeFeatureBlockedModal}
        searchType={searchType}
        paymentShowModalNumber={paymentShowModalNumber}
        setPaymentShowModalNumber={setPaymentShowModalNumber}
      />
    </Box>
  );
};
